/**
 * „final geschlossen" (PV-052 L2, A7): kleines Häkchen an Projekt und Karte, Tooltip mit Art
 * (automatisch / von Hand / Altbestand), Datum und Grund. KFML-Aktion setzen/entfernen.
 * Alles nur bei Flag `befunde`. Rechte ausschließlich über canDoProjectAction.
 */
import {
  type LifecycleStatus,
  type Project,
  type Segment,
  canDoProjectAction,
} from '@projektverwaltung/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useFeatureFlag } from '../../lib/features.js';
import { useReaderMode } from '../../lib/reader.js';
import { useSession } from '../../lib/session.js';
import { useToast } from '../toast.js';
import { Badge } from './badge.js';
import { BEFUNDE_QUERY_KEY } from './befunde/befund-basis.js';
import { useConfirm } from './confirm-dialog.js';
import { Modal } from './modal.js';

export type FinalFelder = Pick<
  Project,
  'final_geschlossen_am' | 'final_geschlossen_von' | 'final_geschlossen_grund' | 'final_altbestand'
>;

/**
 * Prop-Typ für `FinalAktion` — bewusst NICHT der volle `Project` aus `@projektverwaltung/shared`
 * (fehlt z. B. `salesforce_object_id`, `created_at`, `updated_at`). Die Seiten, die diese
 * Komponente einbauen (z. B. `project-detail.tsx`), führen wie andere `components/v2/*`-Dateien
 * (Vorbild `move-phase-modal.tsx`) einen eigenen, schlankeren lokalen `Project`-Typ. Hier reicht
 * die strukturelle Teilmenge, die `canDoProjectAction` (`ProjectScopeShape`) und das Rendering
 * tatsächlich brauchen — jeder vollständige `Project` erfüllt sie automatisch mit.
 */
export type FinalAktionProjekt = FinalFelder & {
  project_id: string;
  segment: string;
  lifecycle_status: LifecycleStatus;
  project_lead_user_id?: string | null | undefined;
  manager_user_id?: string | null | undefined;
  mtb_user_id?: string | null | undefined;
  responsibility?: 'PU' | 'Service' | 'MTB' | null | undefined;
  phase_is_intake?: boolean | undefined;
};

export function FinalBadge({ project }: { project: FinalFelder }) {
  const { t, i18n } = useTranslation();
  const an = useFeatureFlag('befunde');
  if (!an) return null;
  if (!project.final_geschlossen_am) return null;
  const art = project.final_altbestand
    ? t('v2.final.art_altbestand', 'Altbestand')
    : project.final_geschlossen_von
      ? t('v2.final.art_hand', 'von Hand')
      : t('v2.final.art_automatisch', 'automatisch');
  const datum = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
    new Date(project.final_geschlossen_am),
  );
  const titel = `${t('v2.final.tooltip', 'final geschlossen')} · ${art} · ${datum}${
    project.final_geschlossen_grund ? ` — ${project.final_geschlossen_grund}` : ''
  }`;
  return (
    <Badge tone="success" title={titel}>
      {project.final_altbestand
        ? t('v2.final.badge_altbestand', '✓ final (Altbestand)')
        : t('v2.final.badge', '✓ final')}
    </Badge>
  );
}

export function FinalAktion({ project }: { project: FinalAktionProjekt }) {
  const { t } = useTranslation();
  const an = useFeatureFlag('befunde');
  const { user } = useSession();
  const isReader = useReaderMode();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirm();
  const [offen, setOffen] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['v2-project', project.project_id] });
    void queryClient.invalidateQueries({ queryKey: BEFUNDE_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ['v2-kfml-worklist'] });
    // Konvention wie due-date-editor.tsx: Phase-Board hält seine eigene Liste, muss mit
    // aufgefrischt werden, sonst zeigt es das „final"-Häkchen erst nach manuellem Reload.
    void queryClient.invalidateQueries({ queryKey: ['v2-board-projects'] });
  };
  const entfernen = useMutation({
    mutationFn: () => api.clearFinal(project.project_id),
    onSuccess: () => {
      toast.success(t('v2.final.entfernt', '„final" entfernt'));
      invalidate();
    },
    onError: (err) =>
      toast.error(
        t('v2.final.fehler', 'Aktion fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      ),
  });

  if (!an || !user || isReader || project.lifecycle_status !== 'completed') return null;
  const darf = canDoProjectAction(
    user,
    {
      project_id: project.project_id,
      segment: project.segment as Segment,
      project_lead_user_id: project.project_lead_user_id ?? null,
      manager_user_id: project.manager_user_id ?? null,
      mtb_user_id: project.mtb_user_id ?? null,
      responsibility: project.responsibility ?? null,
      phase_is_intake: project.phase_is_intake ?? false,
      lifecycle_status: project.lifecycle_status,
    },
    'projekt_final_setzen',
  );
  if (!darf) return null;

  if (project.final_geschlossen_am) {
    return (
      <button
        type="button"
        disabled={entfernen.isPending}
        onClick={async () => {
          const ok = await confirmAction({
            title: t('v2.final.entfernen_titel', '„final" entfernen?'),
            body: t(
              'v2.final.entfernen_text',
              'Der Automatismus setzt „final" für diesen Abschluss nicht erneut — erst nach Reaktivierung und neuem Abschluss. Liegt der Abschluss weniger als 4 Monate zurück, werden die Befunde wieder geprüft.',
            ),
            confirmLabel: t('v2.final.entfernen_bestaetigen', 'Entfernen'),
          });
          if (ok) entfernen.mutate();
        }}
        className="text-xs px-3 py-1.5 rounded-md shrink-0"
        style={{
          background: 'var(--surface)',
          color: 'var(--ink-2)',
          border: '1px solid var(--line-soft)',
        }}
      >
        {t('v2.final.entfernen', 'Final entfernen')}
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOffen(true)}
        className="text-xs px-3 py-1.5 rounded-md shrink-0"
        style={{
          background: 'var(--surface)',
          color: 'var(--ink-2)',
          border: '1px solid var(--line-soft)',
        }}
      >
        {t('v2.final.setzen', 'Final setzen')}
      </button>
      <FinalSetzenDialog
        open={offen}
        projectId={project.project_id}
        onClose={() => setOffen(false)}
        onDone={invalidate}
      />
    </>
  );
}

function FinalSetzenDialog({
  open,
  projectId,
  onClose,
  onDone,
}: {
  open: boolean;
  projectId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [grund, setGrund] = useState('');
  useEffect(() => {
    if (open) setGrund('');
  }, [open]);
  const mutation = useMutation({
    mutationFn: () => api.setFinal(projectId, grund.trim()),
    onSuccess: () => {
      toast.success(t('v2.final.gesetzt', '„final" gesetzt'));
      onDone();
      onClose();
    },
    onError: (err) =>
      toast.error(
        t('v2.final.fehler', 'Aktion fehlgeschlagen'),
        err instanceof Error ? err.message : undefined,
      ),
  });
  if (!open) return null;
  const bereit = grund.trim().length > 0;
  return (
    <Modal
      open
      onClose={onClose}
      labelledBy={`final-${projectId}`}
      panelClassName="w-full max-w-md overflow-hidden"
      closeOnBackdrop={false}
      closeOnEscape={false}
    >
      <header className="px-5 py-4 border-b" style={{ borderColor: 'var(--line-soft)' }}>
        <h2
          id={`final-${projectId}`}
          className="text-sm font-medium"
          style={{ color: 'var(--ink)' }}
        >
          {t('v2.final.setzen_titel', '„final geschlossen" von Hand setzen')}
        </h2>
      </header>
      <div className="px-5 py-4 space-y-3">
        <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
          {t(
            'v2.final.setzen_text',
            'Für Sonderfälle, in denen eine Abschlussprüfung aus bekanntem Grund nie grün wird. Danach wird das Projekt nicht mehr geprüft; offene Befunde werden erledigt.',
          )}
        </p>
        <label className="block text-xs" style={{ color: 'var(--ink-3)' }}>
          {t('v2.final.grund', 'Begründung *')}
          <textarea
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            rows={3}
            className="mt-1 w-full text-sm px-2 py-1.5 rounded resize-y"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--line-soft)',
              color: 'var(--ink)',
            }}
          />
        </label>
      </div>
      <footer
        className="px-5 py-3 border-t flex items-center justify-end gap-2"
        style={{ borderColor: 'var(--line-soft)', background: 'var(--surface-2)' }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: 'var(--surface)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
          }}
        >
          {t('v2.befund.abbrechen', 'Abbrechen')}
        </button>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!bereit || mutation.isPending}
          className="text-xs px-3 py-1.5 rounded-md disabled:opacity-50"
          style={{ background: 'var(--primary)', color: 'var(--on-primary)', border: 'none' }}
        >
          {t('v2.befund.speichern', 'Speichern')}
        </button>
      </footer>
    </Modal>
  );
}
